import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { FormField } from '../../components/FormField'
import { Breadcrumbs, type Crumb } from '../../components/Breadcrumbs'
import { RecordLink } from '../../components/RecordLink'
import { apiErrorMessage } from '../../lib/errors'
import { ActivationPanel } from '../../components/ActivationPanel'
import { FleetAvailabilityNote } from './FleetStandingCell'
import type { CarrierStanding, FleetStanding } from './fleetStanding'
import { DocumentsPanel } from '../documents/DocumentsPanel'
import { formatPhoneTyping } from '../../lib/phone'
import { addDriver, getCarrier, updateDriver, type DriverPayload } from './api'
import {
  messageFor,
  toDriverFormErrors,
  withoutDriverErrorsFor,
  type FormErrors,
} from './errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'

const emptyForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  cdlNumber: '',
  cdlState: '',
}

type DriverForm = typeof emptyForm

const noErrors: FormErrors = { fields: {}, summary: [] }

/**
 * Adds or edits a driver on a carrier's fleet — a record, not a login: accounts for
 * drivers arrive with the mobile app (Stage 4). Editing loads the carrier detail and
 * picks its row from the fleet; the carrier page is where the save returns.
 */
export function DriverFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: carrierId, driverId } = useParams()
  const isEditing = driverId !== undefined

  const [form, setForm] = useState<DriverForm>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The reading this form was opened on. Set when the record loads and by "load what is
  // saved" — never by the standing re-read below, which would adopt somebody else's
  // version and let the next save overwrite what they wrote.
  const [version, setVersion] = useState<string | null>(null)
  // The trail's names (TRKC-97): the carrier's, and this record's as it is saved —
  // never as it is being typed.
  const [carrierName, setCarrierName] = useState<string | null>(null)
  const [savedName, setSavedName] = useState<string | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Whether we still work with this one. Its own fact, stated by its own button --
  // never a field that rides along with a save of the name and the phone.
  // The carrier's half of the answer. Held rather than inferred: a record whose own
  // papers are incomplete under a carrier we stood down is out of play twice over,
  // and the two are indistinguishable from the child's own fields.
  const [standing, setStanding] = useState<{
    record: FleetStanding
    carrier: CarrierStanding
  } | null>(null)
  const [activation, setActivation] = useState<{ active: boolean; deactivatedAt: string | null }>({
    active: true,
    deactivatedAt: null,
  })

  // The re-read in flight. Two can be asked seconds apart — a paper filed, then an
  // activation toggled — and only the latest may write: an older answer landing last
  // would show a standing the server has already moved past.
  const refreshGeneration = useRef(0)
  // A re-read that failed. Kept apart from the first load's failure, which takes the
  // whole page down: here the write itself succeeded and the form stays exactly as
  // typed — but the note goes, because a standing nobody could re-read is not one to
  // keep showing as if it had been.
  const [standingFailure, setStandingFailure] = useState<{ error: unknown } | null>(null)

  /**
   * The availability note is the server's conclusion, re-read after every write that
   * can move it — an activation change, a paper filed or struck in the panel below —
   * and never guessed at. Re-read on its own, without the form: the driver's fields
   * did not change, and a phone number half typed must not be thrown away because a
   * medical card was filed underneath it (review finding on #13).
   */
  const refreshStanding = useCallback(async () => {
    if (carrierId === undefined || driverId === undefined) {
      return
    }

    refreshGeneration.current += 1
    const generation = refreshGeneration.current
    setStandingFailure(null)

    try {
      const carrier = await getCarrier(carrierId)
      if (generation !== refreshGeneration.current) {
        return
      }

      const driver = carrier.drivers.find((row) => row.id === driverId)
      if (driver === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setActivation({ active: driver.active, deactivatedAt: driver.deactivatedAt })
      setStanding({
        record: driver,
        carrier,
      })
    } catch (caught) {
      if (generation === refreshGeneration.current) {
        setStanding(null)
        setStandingFailure({ error: caught })
      }
    }
  }, [carrierId, driverId])

  useEffect(() => {
    if (carrierId === undefined || driverId === undefined) {
      return
    }

    let active = true
    // A fresh record: a re-read still in flight for the previous one may not write.
    refreshGeneration.current += 1
    setStandingFailure(null)
    setIsLoading(true)
    setLoadFailure(null)

    void (async () => {
      try {
        const carrier = await getCarrier(carrierId)
        const driver = carrier.drivers.find((row) => row.id === driverId)
        if (!active) {
          return
        }

        if (driver === undefined) {
          // The id belongs to no driver of this carrier: same refusal as a broker
          // that could not be read — never an editable blank form.
          setLoadFailure({ error: null })
          return
        }

        setActivation({ active: driver.active, deactivatedAt: driver.deactivatedAt })
        setStanding({
          record: driver,
          carrier,
        })
        setVersion(driver.updatedAt)
        setCarrierName(carrier.name)
        setSavedName(`${driver.firstName} ${driver.lastName}`)
        setForm({
          firstName: driver.firstName,
          lastName: driver.lastName,
          phone: driver.phone ?? '',
          email: driver.email ?? '',
          cdlNumber: driver.cdlNumber ?? '',
          cdlState: driver.cdlState ?? '',
        })
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [carrierId, driverId])

  // A new driver reads nothing else, so its carrier's name is asked for the trail
  // alone. Unread, the step shows an ellipsis: the form works the same without it.
  useEffect(() => {
    if (carrierId === undefined || isEditing) {
      return
    }

    let active = true

    getCarrier(carrierId)
      .then((carrier) => {
        if (active) {
          setCarrierName(carrier.name)
        }
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [carrierId, isEditing])

  if (carrierId === undefined) {
    return null
  }

  function set(field: keyof DriverForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutDriverErrorsFor(current, field))
  }

  /**
   * The deliberate step after a refusal: this driver as it is stored now, into this
   * form. Nothing else on the page is reloaded, and the version comes with it.
   */
  async function loadWhatIsSaved() {
    if (carrierId === undefined || driverId === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const carrier = await getCarrier(carrierId)
      const driver = carrier.drivers.find((row) => row.id === driverId)

      if (driver === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setVersion(driver.updatedAt)
      setCarrierName(carrier.name)
      setSavedName(`${driver.firstName} ${driver.lastName}`)
      setForm({
        firstName: driver.firstName,
        lastName: driver.lastName,
        phone: driver.phone ?? '',
        email: driver.email ?? '',
        cdlNumber: driver.cdlNumber ?? '',
        cdlState: driver.cdlState ?? '',
      })
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toDriverFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    const payload: DriverPayload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: blankToNull(form.phone),
      email: blankToNull(form.email),
      cdlNumber: blankToNull(form.cdlNumber),
      cdlState: blankToNull(form.cdlState),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (driverId === undefined) {
        await addDriver(carrierId!, payload)
      } else {
        await updateDriver(carrierId!, driverId, payload)
      }

      void navigate(`/carriers/${carrierId}`, { replace: true })
    } catch (caught) {
      setErrors(toDriverFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Carriers › the carrier › this record — or the page's own title when the record cannot be read. */
  const trail = (leaf: string | null): Crumb[] => [
    { label: t('nav.carriers'), to: '/carriers' },
    { label: carrierName, to: `/carriers/${carrierId}` },
    { label: leaf },
  ]

  if (isLoading) {
    return <p className="session-check">{t('carriers.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={trail(t('carriers.driverEditTitle'))} />
        <header className="page__header">
          <h1>{t('carriers.driverEditTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
        <p>
          <Link to={`/carriers/${carrierId}`}>{t('carriers.backToCarrier')}</Link>
        </p>
      </section>
    )
  }

  // A license another driver holds: the sentence names that driver, and the way to it
  // rides along — "correct the original first" is one click, the MC number's way.
  const field = (name: keyof DriverForm) => {
    const code = errors.fields[name]

    if (code === undefined) {
      return undefined
    }

    const subject = errors.subjects?.[code]
    const text = messageFor(code, t, 'carriers.saveError', subject)

    return subject === undefined ? text : (
      <>
        {text} <RecordLink subject={subject} />
      </>
    )
  }

  return (
    <section className="page">
      <Breadcrumbs trail={trail(isEditing ? savedName : t('breadcrumbs.newDriver'))} />
      <header className="page__header">
        <h1>{isEditing ? t('carriers.driverEditTitle') : t('carriers.driverTitle')}</h1>
      </header>

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice onLoadSaved={() => void loadWhatIsSaved()} isLoading={isLoadingSaved} />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'carriers.saveError')}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset">
          <legend>{t('carriers.identity')}</legend>

          <FormField
            name="firstName"
            label={t('carriers.firstName')}
            value={form.firstName}
            onChange={(value) => set('firstName', value)}
            error={field('firstName')}
            maxLength={100}
            required
          />
          <FormField
            name="lastName"
            label={t('carriers.lastName')}
            value={form.lastName}
            onChange={(value) => set('lastName', value)}
            error={field('lastName')}
            maxLength={100}
            required
          />
          <FormField
            name="cdlNumber"
            label={t('carriers.cdlNumber')}
            value={form.cdlNumber}
            // Uppercased as typed, the way the license prints it.
            onChange={(value) => set('cdlNumber', value.toUpperCase())}
            error={field('cdlNumber')}
            maxLength={32}
          />
          {/* Half the license (TRKC-97): a state prints its own numbers, so a number is
              one license only beside the state that issued it. Asked for whenever a
              number is typed — the server says so on this box. */}
          <FormField
            name="cdlState"
            label={t('carriers.cdlState')}
            value={form.cdlState}
            onChange={(value) => set('cdlState', value.toUpperCase().replace(/[^A-Z]/g, ''))}
            error={field('cdlState')}
            maxLength={2}
            placeholder="TX"
            width="narrow"
          />
          <FormField
            name="phone"
            label={t('carriers.phone')}
            value={form.phone}
            onChange={(value) => set('phone', formatPhoneTyping(form.phone, value))}
            error={field('phone')}
            type="tel"
            placeholder="(305) 555-0142"
          />
          <FormField
            name="email"
            label={t('carriers.email')}
            value={form.email}
            onChange={(value) => set('email', value)}
            error={field('email')}
            type="email"
            maxLength={256}
          />
        </fieldset>

        <div className="form__actions">
          <Link className="button button--quiet" to={`/carriers/${carrierId}`}>
            {t('carriers.backToCarrier')}
          </Link>
          <button type="submit" className="button" disabled={isSubmitting}>
            {isSubmitting ? t('carriers.saving') : t('carriers.saveDriver')}
          </button>
        </div>
      </form>

      {/* The CDL and medical card live here, on the person they certify — and filing
          one can move his standing, so the note below is re-read when it does. */}
      {driverId !== undefined && (
        <DocumentsPanel
          ownerType="Driver"
          ownerId={driverId}
          onOwnerStandingChanged={() => void refreshStanding()}
        />
      )}
      {driverId !== undefined && (
        <>
          {standing !== null && (
            <FleetAvailabilityNote
              record={standing.record}
              carrier={standing.carrier}
            />
          )}
          {standingFailure !== null && (
            <p className="alert alert--warning" role="status">
              {apiErrorMessage(standingFailure.error, t, 'compliance.standingUnread')}
            </p>
          )}
          <ActivationPanel
            ownerType="Driver"
            ownerId={driverId}
            active={activation.active}
            deactivatedAt={activation.deactivatedAt}
            onChanged={(active, deactivatedAt) => {
              setActivation({ active, deactivatedAt })
              void refreshStanding()
            }}
          />
        </>
      )}
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
